import React, { useEffect, useState } from 'react'; // Ensure React is imported correctly
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  Row,
  Select,
  Form,
  Spin,
  TimePicker,
  Card,
  message,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { editProduction, fetchProductionById } from '../../slices/production';
import AutoOpenSelect from '../SelectComponent/AutoOpenSelect';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import { SaveOutlined } from '@ant-design/icons';
import { fetchPlanningList, resetPlanningList } from '../../slices/Planning';
dayjs.extend(utc);
dayjs.extend(duration);

const EditProduction = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const { Option } = Select;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [selectedPress, setSelectedPress] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shiftModes, setShiftModes] = useState([
    { title: 'A', value: 'A' },
    { title: 'B', value: 'B' },
  ]);
  const [shiftmode, setShiftMode] = useState(false);
  const [selectedShift, setSelectedShift] = useState('');

  const getCurrentShift = () => {
    const now = dayjs().tz('Asia/Kolkata');

    const startA = now.set('hour', 8).set('minute', 0).set('second', 0); // 08:00 AM
    const endA = now.set('hour', 20).set('minute', 0).set('second', 0); // 08:00 PM

    if (now.isSameOrAfter(startA) && now.isBefore(endA)) {
      return 'A';
    }

    return 'B';
  };

  useEffect(() => {
    const currentShift = getCurrentShift();
    console.log(currentShift);
    setSelectedShift(currentShift);
    form.setFieldsValue({
      shift: currentShift, // for example
    });
  }, []);
  const handleChangeShift = e => {
    setShiftMode(e);
  };


  const { list: PlanningList = [] } = useSelector(state => state.Planning);

  const timeIn = Form.useWatch('time_in', form);
  const timeOut = Form.useWatch('time_out', form);
  const billetNos = Form.useWatch('billet_nos', form);
  const billetSize = Form.useWatch('billet_size', form);
  const pieces = Form.useWatch('pieces', form);
  const weightPerPiece = Form.useWatch('weight_per_piece', form);
  const kgPerHour = Form.useWatch('kg_per_hour', form);
  const powerCutAt = Form.useWatch('power_cut_at', form);
  const powerResumedAt = Form.useWatch('power_resumed_at', form);
  const billetWeight = Form.useWatch('billet_weight', form);
  const weight = Form.useWatch('weight', form);

  useEffect(() => {
    dispatch(resetPlanningList());
    dispatch(fetchPlanningList({ page: 1, pagesize: 5000 }));

  }, [dispatch, search, orderBy]);

  // Fetch production data by ID
  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        message.error('Invalid production ID.');
        return;
      }
      try {
        const response = await dispatch(fetchProductionById({ id })).unwrap();
        const responsedData = response?.data?.data;
        console.log(responsedData)
        if (responsedData) {
          setSelectedPress(responsedData?.press);
          const workOrderId = responsedData?.iwo_no?.id;
          const workorderLabel = [
            responsedData?.iwo_no?.order_no,
            responsedData?.iwo_no?.order_date,          // if exists
            responsedData?.iwo_no?.purchase_order_no,
            responsedData?.iwo_no?.purchase_order_date,
            responsedData?.iwo_no?.bill_to?.customer_name,      // if exists
          ].filter(Boolean).join(' - ');
          form.setFieldsValue({
            planning: responsedData.planning?.id || null,
            shift: responsedData.shift || null,
            press: { value: responsedData?.press?.id, label: responsedData?.press?.name },
            iwo_no: { value: workOrderId, label: workorderLabel },
            customer: { value: responsedData?.customer?.id, label: responsedData?.customer?.customer_name },
            die_profile: responsedData.die_profile
              ? {
                value: responsedData.die_profile?.id,
                label: responsedData.die_profile?.die_number,
              }
              : null,
            cavity: responsedData.cavity || null,
            time_in: responsedData.time_in
              ? dayjs(responsedData.time_in, 'HH:mm:ss')
              : null,
            time_out: responsedData.time_out
              ? dayjs(responsedData.time_out, 'HH:mm:ss')
              : null,
            total_cycle: responsedData.total_cycle || null,
            billet_size: responsedData.billet_size || null,
            billet_nos: responsedData.billet_nos || null,
            billet_weight: responsedData.billet_weight || null,
            cut_length: responsedData.cut_length || null,
            pieces: responsedData.pieces || null,
            weight_per_piece: responsedData.weight_per_piece || null,
            weight: responsedData.weight || null,
            recovery: responsedData.recovery || null,
            scrap: responsedData.scrap || null,
            speed: responsedData.speed || null,
            kg_per_hour: responsedData.kg_per_hour || null,
            ext_pressure: responsedData.ext_pressure || null,
            basket_no: responsedData.basket_no || null,
            alloy: { value: responsedData?.alloy?.id, label: responsedData?.alloy?.alloy_code },
            cast_no: responsedData.cast_no || null,
            target: responsedData.target || null,
            bloster_no: { value: responsedData?.bloster?.id, label: responsedData?.bloster?.bloster_no },
            remarks: responsedData.remarks || null,
            production_supervisor: responsedData.production_supervisor || null,
            power_cut_at: responsedData.power_cut_at
              ? dayjs(responsedData.power_cut_at, 'HH:mm:ss')
              : null,
            power_resumed_at: responsedData.power_resumed_at
              ? dayjs(responsedData.power_resumed_at, 'HH:mm:ss')
              : null,
            power_cut_duration: responsedData.power_cut_duration || null,
          });

        }
      } catch (error) {
        message.error('Failed to fetch production data. Please try again.');
        console.error('Error fetching production:', error);
      }
    };
    fetchData();
  }, [id, dispatch, form]);


  // Update calculated fields
  useEffect(() => {
    if (timeIn && timeOut && dayjs(timeOut).isAfter(dayjs(timeIn))) {
      const diffInSeconds = dayjs(timeOut).diff(dayjs(timeIn), 'second');
      const hours = Math.floor(diffInSeconds / 3600);
      const minutes = Math.floor((diffInSeconds % 3600) / 60);
      const seconds = diffInSeconds % 60;
      const formatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      form.setFieldsValue({ total_cycle: formatted });
    } else {
      form.setFieldsValue({ total_cycle: '' });
    }

    if (
      powerCutAt &&
      powerResumedAt &&
      dayjs(powerResumedAt).isAfter(dayjs(powerCutAt))
    ) {
      const diffInSeconds = dayjs(powerResumedAt).diff(
        dayjs(powerCutAt),
        'second',
      );
      const hours = Math.floor(diffInSeconds / 3600);
      const minutes = Math.floor((diffInSeconds % 3600) / 60);
      const seconds = diffInSeconds % 60;
      const formatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      form.setFieldsValue({ power_cut_duration: formatted });
    } else {
      form.setFieldsValue({ power_cut_duration: '' });
    }

    // if (billetNos && billetSize) {
    //   const size = parseInt(billetSize);
    //   const nos = parseInt(billetNos);
    //   const weight = parseInt(0.034 * size * nos + 10);
    //   form.setFieldsValue({ billet_weight: weight });
    // }
    if (billetNos && billetSize && selectedPress?.billet_wt_factor) {
      const size = Number(billetSize);
      const nos = Number(billetNos);
      const factor = parseFloat(selectedPress.billet_wt_factor); // "0.024" → 0.024

      const singleBilletWt = size * factor;
      const totalWeight = Math.round(singleBilletWt * nos);

      form.setFieldsValue({ billet_weight: totalWeight });
    }

    if (pieces && weightPerPiece) {
      const totalWeight = parseInt(parseFloat(weightPerPiece) * parseInt(pieces));
      form.setFieldsValue({ weight: totalWeight });
    }

    // if (weight && billetWeight && parseFloat(billetWeight) > 0) {
    //   const recovery = ((Number(weight) / Number(billetWeight)) * 100).toFixed(2);
    //   form.setFieldsValue({ recovery });
    // } else {
    //   form.setFieldsValue({ recovery: '' });
    // }
    if (weight && billetWeight && Number(billetWeight) > 0) {
      let recovery = (Number(weight) / Number(billetWeight)) * 100;

      // Business rule: cannot exceed 100%
      if (recovery > 100) recovery = 100;

      form.setFieldsValue({
        recovery: recovery.toFixed(2)
      });
    }

    if (billetWeight && weight) {
      const scrap = parseFloat(billetWeight) - parseFloat(weight);
      form.setFieldsValue({ scrap: scrap >= 0 ? scrap.toFixed(2) : '' });
    } else {
      form.setFieldsValue({ scrap: '' });
    }

    if (weight && timeIn && timeOut && dayjs(timeOut).isAfter(dayjs(timeIn))) {
      const diffInMinutes = dayjs(timeOut).diff(dayjs(timeIn), 'minute');
      console.log('Total minutes:', diffInMinutes);

      if (diffInMinutes > 0) {
        const result = weight / (diffInMinutes / 60);
        console.log('results', result);
        form.setFieldsValue({ kg_per_hour: Math.round(result) });
      } else {
        form.setFieldsValue({ kg_per_hour: '' });
      }
    }

    if (kgPerHour) {
      form.setFieldsValue({ target: kgPerHour >= 400 ? 'YES' : 'NO' });
    }
  }, [
    timeIn,
    timeOut,
    billetNos,
    billetSize,
    pieces,
    weightPerPiece,
    kgPerHour,
    powerCutAt,
    powerResumedAt,
    billetWeight,
    weight,
    form,
    selectedPress
  ]);

  const onFinish = async values => {
    setIsSubmitting(true);
    try {
      const payload = {
        planning: values.planning || null,
        shift: values.shift || null,
        press: values.press?.value || null,
        iwo_no: values?.iwo_no?.value,
        customer: values.customer?.value || null,
        die_profile: values.die_profile.value,
        cavity: values.cavity ? values.cavity : null,
        time_in: values.time_in
          ? dayjs(values.time_in).format('HH:mm:ss')
          : null,
        time_out: values.time_out
          ? dayjs(values.time_out).format('HH:mm:ss')
          : null,
        total_cycle: values.total_cycle || null,
        billet_size: values.billet_size ? parseInt(values.billet_size) : null,
        billet_nos: values.billet_nos ? parseInt(values.billet_nos) : null,
        billet_weight: values.billet_weight
          ? parseFloat(values.billet_weight)
          : null,
        cut_length: values.cut_length || null,
        pieces: values.pieces ? parseInt(values.pieces) : null,
        weight_per_piece: values.weight_per_piece
          ? parseFloat(values.weight_per_piece)
          : null,
        weight: values.weight ? parseFloat(values.weight) : null,
        recovery: values.recovery ? parseFloat(values.recovery) : null,
        scrap: values.scrap ? parseFloat(values.scrap) : null,
        speed: values.speed ? parseFloat(values.speed) : null,
        kg_per_hour: values.kg_per_hour ? parseFloat(values.kg_per_hour) : null,
        ext_pressure: values.ext_pressure
          ? parseFloat(values.ext_pressure)
          : null,
        basket_no: values.basket_no ? parseInt(values.basket_no) : null,
        alloy: values.alloy?.value || null,
        cast_no: values.cast_no || null,
        target: values.target || null,
        bloster_no: values.bloster_no?.value || null,
        remarks: values.remarks || null,
        production_supervisor: values.production_supervisor || null,
        power_cut_at: values.power_cut_at
          ? dayjs(values.power_cut_at).format('HH:mm:ss')
          : null,
        power_resumed_at: values.power_resumed_at
          ? dayjs(values.power_resumed_at).format('HH:mm:ss')
          : null,
        power_cut_duration: values.power_cut_duration || null,
      };

      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      await dispatch(editProduction({ id, payload })).unwrap();
      message.success('Production record updated successfully!');
      navigate('/production-list');
    } catch (error) {
      console.error('Error updating production record:', error);
      message.error('Failed to update production record. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlanningChange = plan => {
    console.log(plan);
    if (plan) {
      const { selected_row, profile_no, workorder, die_tool } = plan;
      console.log('selectedrow', selected_row || null);
      console.log('profile', profile_no || null);
      console.log("workorder", workorder);
      console.log("die_tool", die_tool)
      const workOrderId = workorder?.id;
      const workorderLabel = [
        workorder?.order_no,
        workorder?.order_date,          // if exists
        workorder?.purchase_order_no,
        workorder?.purchase_order_date,
        workorder?.bill_to?.customer_name,      // if exists
      ].filter(Boolean).join(' - ');

      form.setFieldsValue({
        iwo_no: { value: workOrderId, label: workorderLabel },
        customer: { value: workorder?.bill_to?.id, label: workorder?.bill_to?.customer_name },
        die_profile: { value: profile_no?.id, label: profile_no?.die_number },
        press: { value: die_tool?.eligible_for_press?.id, label: die_tool?.eligible_for_press?.name },
        cavity: die_tool?.die_cavity || null,
        billet_size: selected_row.bltSize || null,
        billet_nos: selected_row.totalBillets || null,
        billet_weight: selected_row?.totalKgs || null,
        cut_length: workorder?.work_order_details[0]?.length || null,
        pieces: parseInt(plan?.plan_pcs) || null,
        recovery: selected_row.recovery || null,
        alloy: { value: workorder?.work_order_details[0]?.alloy?.id, label: workorder?.work_order_details[0]?.alloy?.alloy_code },
        bloster_no: { value: die_tool?.eligible_bloster?.id, label: die_tool?.eligible_bloster?.bloster_no },
      });
    }
  };

  return (
    <div className="sticyaction">
      <Row className="pagenamerow mb-0" justify="space-between" align="middle">
        <Col>
          <h2>Edit Production Report</h2>
          <div className="bredcrumbwrp">
            <Link onClick={() => navigate(-1)} className="back">
              <Back /> BACK
            </Link>
            <Breadcrumb
              items={[
                { title: <Link to="/production-list">Production List</Link> },
                { title: 'Edit Production' },
              ]}
            />
          </div>
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ marginTop: '20px' }}
      >
        <Card style={{ marginBottom: '20px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={4}>
              <Form.Item
                name="planning"
                label="Select Planning"
                rules={[{ required: true, message: 'Please Select Planning' }]}
              >
                <Select
                  placeholder="Select Planning"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  onChange={(value, option) => {
                    form.setFieldValue('planning', value); // ID stored in form
                    handlePlanningChange(option?.plan);            // FULL OBJECT
                  }}
                >
                  {PlanningList?.map(plan => (
                    <Option key={plan?.id} value={plan?.id} plan={plan}>
                      {plan.planning_no} -{' '} {plan?.profile_no?.die_number} -{' '}
                      {dayjs(plan.planning_date).format('DD/MM/YYYY')}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item
                name="iwo_no"
                label="Work Order"
                rules={[{ required: true, message: 'Please Select IWO No.' }]}
              >
                <AutoOpenSelect
                  placeholder="Order No - Order Date - PO Number - PO Date - Customer Name"
                  disabled
                  open={false}              // 👈 prevents dropdown
                  showSearch={false}        // 👈 no search
                  dropdownRender={() => null} // 
                >
                </AutoOpenSelect>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Form.Item
                label="Shift"
                name="shift"
                rules={[{ required: true, message: 'Please select shift' }]}
              >
                <Select
                  placeholder="Select Shift"
                  onChange={handleChangeShift}
                  options={shiftModes}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <Form.Item
                name="customer"
                label="Customer"
                rules={[{ required: true, message: 'Please select customer' }]}
              >
                <AutoOpenSelect
                  placeholder="Customer Name"
                  disabled
                  open={false}              // 👈 prevents dropdown
                  showSearch={false}        // 👈 no search
                  dropdownRender={() => null} // 
                >
                </AutoOpenSelect>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Form.Item
                name="die_profile"
                label="Profile Number"
                rules={[
                  { required: true, message: 'Please select a profile number' },
                ]}
              >
                <AutoOpenSelect
                  placeholder="Select Profile No"
                  style={{ width: '100%' }}
                  labelInValue
                  disabled
                  open={false}              // 👈 prevents dropdown
                  showSearch={false}        // 👈 no search
                  dropdownRender={() => null} // 
                >

                </AutoOpenSelect>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Form.Item
                name="press"
                label="Press"
                rules={[{ required: true, message: 'Please Select Press' }]}
              >
                <AutoOpenSelect
                  placeholder="Select Press "
                  style={{ width: '100%' }}
                  labelInValue
                  disabled
                  open={false}              // 👈 prevents dropdown
                  showSearch={false}        // 👈 no search
                  dropdownRender={() => null} // 
                >

                </AutoOpenSelect>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Form.Item label="Cavity" name="cavity">
                <Input readOnly disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Time In" name="time_in" rules={rules.timeIn}>
                <TimePicker
                  use12Hours
                  format="h:mm:ss A"
                  placeholder="Select Time In"
                  style={{ width: '100%' }}
                  minuteStep={1}
                  secondStep={1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Time Out" name="time_out" rules={rules.timeOut}>
                <TimePicker
                  use12Hours
                  format="h:mm:ss A"
                  placeholder="Select Time Out"
                  style={{ width: '100%' }}
                  minuteStep={1}
                  secondStep={1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item
                label="Total Cycle (HH:mm:ss)"
                name="total_cycle"
                rules={rules.totalCycle}
              >
                <Input readOnly disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item
                label="Billet Size"
                name="billet_size"
                rules={rules.billetSize}
              >
                <Input placeholder="Enter Size" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item
                label="Billet Nos"
                name="billet_nos"
                rules={rules.billetNos}
              >
                <Input placeholder="Enter Nos" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Billet Weight" name="billet_weight">
                <Input readOnly disabled placeholder="Enter Weight" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Cut Length" name="cut_length">
                <Input placeholder="Enter Cut Length" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Pieces" name="pieces" rules={rules.pieces}>
                <Input placeholder="Enter Pieces" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item
                label="Weight / Pc"
                name="weight_per_piece"
                rules={rules.weightPerPiece}
              >
                <Input placeholder="Enter Weight / Pc" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Weight Output" name="weight">
                <Input readOnly disabled placeholder="Enter Weight Output" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Recovery %" name="recovery">
                <Input readOnly disabled placeholder="Enter Recovery %" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Scrap" name="scrap">
                <Input placeholder="Enter Scrap" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Speed" name="speed">
                <Input placeholder="Enter Speed" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Kg / Hr" name="kg_per_hour">
                <Input placeholder="Enter Kg / Hr" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Extrusion Pressure" name="ext_pressure" rules={[
                { required: true, message: 'Please Enter Extrusion Pressure.' },
                { pattern: /^\d+$/, message: 'Please enter a valid number' },
              ]}>
                <Input placeholder="Enter Extrusion Pressure" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Basket No" name="basket_no" rules={[
                { required: true, message: 'Please Enter Basket No.' },
              ]}>
                <Input placeholder="Enter Basket No" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Alloy" name="alloy">
                <AutoOpenSelect
                  placeholder="Select Alloy"
                  labelInValue
                  disabled
                  open={false}
                  dropdownRender={() => null}
                >
                </AutoOpenSelect>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Cast No." name="cast_no">
                <Input placeholder="Enter Cast No." />
              </Form.Item>
            </Col>
            {/* <Col xs={24} sm={12} md={4}>
              <Form.Item label="Target 400" name="target">
                <Input readOnly disabled placeholder="Enter Target 400" />
              </Form.Item>
            </Col> */}
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Bolster No" name="bloster_no" rules={[
                { required: true, message: 'Please Enter Bloster No' }
              ]}>
                <AutoOpenSelect
                  placeholder="Select Bolster"
                  labelInValue
                  disabled
                  open={false}
                  dropdownRender={() => null}
                >
                </AutoOpenSelect>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Supervisor Name" name="production_supervisor" rules={[
                { required: true, message: 'Please Supervisor Name' },
              ]}>
                <Input placeholder="Enter Supervisor Name" />
              </Form.Item>
            </Col>
            {/* <Col xs={24} sm={12} md={4}>
              <Form.Item label="Power Cut At" name="power_cut_at">
                <TimePicker
                  use12Hours
                  format="h:mm:ss A"
                  placeholder="Select Power Cut At"
                  style={{ width: '100%' }}
                  minuteStep={1}
                  secondStep={1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label="Power Resumed At" name="power_resumed_at">
                <TimePicker
                  use12Hours
                  format="h:mm:ss A"
                  placeholder="Select Resumed At"
                  style={{ width: '100%' }}
                  minuteStep={1}
                  secondStep={1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item
                label="Power Cut Duration (HH:mm:ss)"
                name="power_cut_duration"
              >
                <Input
                  readOnly
                  disabled
                  placeholder="Enter Total Power Cut Duration"
                />
              </Form.Item>
            </Col> */}
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Remarks" name="remarks">
                <Input.TextArea placeholder="Enter Remarks" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: '#fff',
            padding: '16px',
            textAlign: 'right',
          }}
        >
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={isSubmitting}
          >
            Edit Production Record
          </Button>
        </div>
      </Form>
    </div>
  );
};

const rules = {
  timeIn: [{ required: true, message: 'Please Select Time In.' }],
  timeOut: [{ required: true, message: 'Please Select Time Out.' }],
  totalCycle: [{ required: true, message: 'Please Select Total Cycle.' }],
  billetSize: [
    { required: true, message: 'Please Enter Billet Size.' },
    { pattern: /^\d+$/, message: 'Please enter a valid number' },
  ],
  billetNos: [
    { required: true, message: 'Please Enter Billet Nos.' },
    { pattern: /^\d+$/, message: 'Please enter a valid number' },
  ],
  pieces: [
    { required: true, message: 'Please Enter Pieces.' },
    { pattern: /^\d+$/, message: 'Please enter a valid number' },
  ],
  weightPerPiece: [
    { required: true, message: 'Please Enter Weight / Pc.' },
    { pattern: /^\d*\.?\d+$/, message: 'Please enter a valid number' },
  ],
};

export default EditProduction;